import BlockScreen from "common/BlockScreen";
import MaintenanceScreen from "common/MaintenanceScreen";
import { usePages } from "features/pages/usePages";
import { indexPagesByPath } from "features/pages/utils";
import { useFlag, useFlags } from "hooks/useFlags";
import { useRefetchStaleQueriesOnNavigate } from "hooks/useRefetchStaleQueriesOnNavigate";
import { useUser } from "hooks/useUser";
import React, { useEffect, useMemo } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import LoadingPlaceholder from "ui/LoadingPlaceholder";

import CatalogPage from "./pages/cases/CatalogPage";
import NewProblemPage from "./pages/cases/NewProblemPage";
import NewSolutionPage from "./pages/cases/NewSolutionPage";
import ProblemPage from "./pages/cases/ProblemPage";
import SolutionPage from "./pages/cases/SolutionPage";
import CatchAllPage from "./pages/CatchAllPage";
import EventRegistrationPage from "./pages/events/EventRegistrationPage";
import LinkResolverPage from "./pages/LinkResolverPage";
import MainPage from "./pages/MainPage";
import ProfilePage from "./pages/my/ProfilePage";
import PeoplePage from "./pages/people/PeoplePage";
import PersonPage from "./pages/people/PersonPage";

const AppRoutes: React.FC = () => {
  const { loading } = useFlags();
  const { isBlocked, isBlockedResriction, isModerator, isAdmin } = useUser();
  const menuHeaderEnabled = useFlag("menuHeaderTest");
  const maintenanceModeEnabled = useFlag("maintenanceModeEnabled");

  const pages = usePages();
  const pagesByPath = useMemo(() => indexPagesByPath(pages ?? []), [pages]);

  useRefetchStaleQueriesOnNavigate();

  // Некоторые клиенты (мессенджеры, автолинкификаторы) percent-encode "#",
  // если он не первый символ в ссылке — второй, "настоящий" хэш из HashRouter
  // вида ".../#/problems#solution-4" превращается в ".../#/problems%23solution-4"
  // и роутер не может разобрать путь (страница/карточка не находятся). Чиним
  // это один раз при загрузке, до того как Routes попытаются сматчить путь.
  useEffect(() => {
    const rawHash = window.location.hash;

    if (/%23/i.test(rawHash)) {
      window.location.hash = rawHash.replace(/%23/gi, "#");
    }
  }, []);

  if (loading || !pages) return <LoadingPlaceholder />;

  // Админы и модераторы видят сайт (только чтение — запись отклоняет бэкенд)
  if (maintenanceModeEnabled && !isAdmin && !isModerator) {
    return <MaintenanceScreen />;
  }

  if (isBlocked) {
    return (
      <Routes>
        <Route
          path="/profile"
          element={<ProfilePage />}
        />
        <Route
          path="*"
          element={<BlockScreen restriction={isBlockedResriction} />}
        />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/problems">
        <Route
          index
          element={<CatalogPage />}
        />
        <Route
          path="add"
          element={<NewProblemPage />}
        />
        <Route
          path=":problemId"
          element={<ProblemPage />}
        />
        <Route
          path=":problemId/solutions/add"
          element={<NewSolutionPage />}
        />
        <Route
          path=":problemId/solutions/:solutionId"
          element={<SolutionPage />}
        />
      </Route>

      <Route path="/people">
        <Route
          index
          element={
            isModerator ? (
              <PeoplePage />
            ) : (
              <Navigate
                to="/"
                replace
              />
            )
          }
        />
        <Route
          path=":personId"
          element={<PersonPage />}
        />
      </Route>

      <Route path="/events">
        <Route
          path=":eventId/reg"
          element={<EventRegistrationPage />}
        />
      </Route>

      <Route
        path="/r/*"
        element={<LinkResolverPage />}
      />

      {/* Отдельные страницы редакторской/модераторской переехали во вкладки
          профиля — старые ссылки редиректим в соответствующую вкладку. */}
      <Route
        path="editorial"
        element={
          <Navigate
            to="/profile?tab=editorial"
            replace
          />
        }
      />
      <Route
        path="moderatorial"
        element={
          <Navigate
            to="/profile?tab=moderatorial"
            replace
          />
        }
      />

      <Route
        path="/profile"
        element={<ProfilePage />}
      />
      <Route
        path="/catalog"
        element={<CatalogPage embedded />}
      />
      <Route
        path="/main"
        element={
          menuHeaderEnabled ? (
            <Navigate
              to="/"
              replace
            />
          ) : (
            <MainPage />
          )
        }
      />
      <Route
        path="/main-page-test"
        element={
          menuHeaderEnabled ? (
            <Navigate
              to="/"
              replace
            />
          ) : (
            <MainPage />
          )
        }
      />
      <Route
        path="/"
        element={
          menuHeaderEnabled ? (
            <CatchAllPage pagesByPath={pagesByPath} />
          ) : (
            <CatalogPage />
          )
        }
      />

      <Route
        path="*"
        element={<CatchAllPage pagesByPath={pagesByPath} />}
      />
    </Routes>
  );
};

export default AppRoutes;
